package com.siriusxm.pia.components

import androidx.compose.runtime.Composable
import com.siriusxm.cmc.utils.Navigator
import com.siriusxm.pia.SXMUI
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

object BreadCrumbStyles : StyleSheet() {
    val crumbs by style {
        fontSize(14.px)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Normal)
    }

    val crumb by style {
        display(DisplayStyle.InlineBlock)
    }

    val selectedCrumb by style {
        fontWeight(700)
    }

    val crumbDivider by style {
    }

    val clickableCrumb by style {
        cursor("pointer")
        color(SXMUI.linkColor.value())
        self + hover style {
            textDecoration("underline")
        }
    }
}

interface BreadcrumbConfiguration {
    fun crumb(name: String, path: String?, child: BreadcrumbConfiguration.() -> Unit = {})
}

private class Breadcrumb(
    val name: String,
    val path: String?
)

private class BreadCrumbs(
    val crumbs: MutableList<Breadcrumb> = mutableListOf(),
    val basePath: String = ""
) : BreadcrumbConfiguration {
    override fun crumb(name: String, path: String?, child: BreadcrumbConfiguration.() -> Unit) {
        val newPath = path?.let { basePath + it }
        crumbs.add(Breadcrumb(name, newPath))

        BreadCrumbs(crumbs, newPath?.let { "$it/" } ?: "").child()
    }
}

/**
 * Renders a breadcrumb navigation bar. When a navigator is provided, breadcrumb clicks
 * will use the navigator for navigation (useful for iframe-embedded applications that need
 * to update the parent window's URL). When no navigator is provided, standard anchor tags
 * with hash-based hrefs are used.
 *
 * @param navigator Optional navigator to handle breadcrumb clicks. If provided, click handlers
 *                  are used instead of anchor hrefs.
 * @param config Configuration block for defining the breadcrumb hierarchy.
 */
@Composable
fun breadcrumbs(navigator: Navigator? = null, config: BreadcrumbConfiguration.() -> Unit) {
    val crumbs = BreadCrumbs()
    crumbs.config()

    Div({
        classes(BreadCrumbStyles.crumbs)
    }) {
        crumbs.crumbs.forEachIndexed { index, crumb ->
            if (index != 0) {
                Span({ classes(BreadCrumbStyles.crumbDivider) }) {
                    svgIcon(SVGIcons.chevronRight) {
                        size = 16.px
                    }
                }
            }
            Span({
                classes(BreadCrumbStyles.crumb)
                if (index == crumbs.crumbs.size - 1 && crumb.path == null) {
                    classes(BreadCrumbStyles.selectedCrumb)
                }
            }) {
                if (crumb.path != null) {
                    if (navigator != null) {
                        // Use click handler with navigator for iframe-embedded apps
                        Span({
                            classes(BreadCrumbStyles.clickableCrumb)
                            onClick {
                                navigator.navigate(crumb.path)
                            }
                        }) {
                            Text(crumb.name)
                        }
                    } else {
                        // Standard anchor tag navigation
                        A(href = "#${crumb.path}") {
                            Text(crumb.name)
                        }
                    }
                } else {
                    Text(crumb.name)
                }
            }
        }
    }
}

fun String.truncate(maxLength: Int): String {
    require(maxLength >= 3) { "maxLength must be at least 3 to accommodate the ellipsis" }
    return if (this.length <= maxLength) this else this.take(maxLength - 3) + "..."
}